import {
  GROWUP_CHANNEL,
  channelWeekSchema,
  type ChannelSlot,
  type ChannelWeek,
} from '@growup/streaming-shared';
import { Button, Card, DateTimeInput, Field, Input, Notice, StatusBadge } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDate, formatDateTime, formatTime } from '../format';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';

const dayFormat = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'Europe/Lisbon',
  weekday: 'long',
  day: '2-digit',
  month: '2-digit',
});
const lisbonDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' });

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const slotWho = (s: ChannelSlot) =>
  s.streamer ? `${s.streamer.displayName} (twitch.tv/${s.streamer.channel})` : 'Programa da casa';

/**
 * Canal Grow uP (GS16, `streaming.channel`): the week on the channel
 * (growupesportstv), new shows, and the streamers' requests to answer
 * (accept, decline with a reason, or propose another time).
 */
export function ChannelView() {
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [week, setWeek] = useState<ChannelWeek | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(`/api/streaming/channel${from ? `?from=${from}` : ''}`, channelWeekSchema)
      .then((w) => {
        setWeek(w);
        setError(null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [from]);
  useEffect(load, [load]);

  const cancel = async (slot: ChannelSlot) => {
    setError(null);
    try {
      await send('DELETE', `/api/streaming/channel/slots/${slot.id}`);
      load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const days = week ? Array.from({ length: 7 }, (_, i) => addDays(week.from, i)) : [];

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {week && week.requests.length > 0 ? (
        <Card
          headingLevel={2}
          title={`Pedidos por responder (${String(week.requests.length)})`}
          eyebrow="Os streamers pedem horário no canal pelo painel"
        >
          <ul className="flex flex-col gap-4">
            {week.requests.map((r) => (
              <RequestRow key={r.id} request={r} onDone={load} />
            ))}
          </ul>
        </Card>
      ) : null}

      <Card
        headingLevel={2}
        title="Grelha do canal"
        eyebrow={`twitch.tv/${GROWUP_CHANNEL}`}
        actions={
          week ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFrom(addDays(week.from, -7))}>
                ← Semana anterior
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFrom(addDays(week.from, 7))}>
                Semana seguinte →
              </Button>
            </div>
          ) : undefined
        }
      >
        {week === null ? <p className="text-gu-ink-muted">A carregar…</p> : null}
        <ol className="flex flex-col gap-3" aria-label="Grelha da semana">
          {days.map((day) => {
            const items = (week?.slots ?? []).filter(
              (s) => lisbonDay.format(new Date(s.startsAt)) === day,
            );
            return (
              <li key={day} className="flex flex-col gap-1 border-b border-gu-line pb-2">
                <h3 className="font-semibold capitalize">
                  {dayFormat.format(new Date(`${day}T12:00:00Z`))}
                </h3>
                {items.length === 0 ? (
                  <p className="text-caption text-gu-ink-muted">Nada marcado.</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {items.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold tabular-nums">
                          {formatTime(s.startsAt)}–{formatTime(s.endsAt)}
                        </span>
                        <span>{s.title}</span>
                        <span className="text-caption text-gu-ink-muted">{slotWho(s)}</span>
                        {s.status === 'requested' ? (
                          <StatusBadge tone="warning">Pedido</StatusBadge>
                        ) : null}
                        {s.status === 'proposed' ? (
                          <StatusBadge tone="info">Proposta enviada</StatusBadge>
                        ) : null}
                        {s.status === 'accepted' ? (
                          <Button variant="ghost" size="sm" onClick={() => void cancel(s)}>
                            Cancelar
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      </Card>

      <NewSlot onCreated={load} />
    </div>
  );
}

function RequestRow({ request, onDone }: { request: ChannelSlot; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [proposing, setProposing] = useState(false);
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(request.startsAt));
  const [endsAt, setEndsAt] = useState(isoToLisbonInput(request.endsAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: 'accept' | 'decline' | 'propose') => {
    setBusy(true);
    setError(null);
    try {
      await send('POST', `/api/streaming/channel/requests/${request.id}/decision`, {
        decision,
        note,
        ...(decision === 'propose'
          ? { startsAt: lisbonInputToIso(startsAt), endsAt: lisbonInputToIso(endsAt) }
          : {}),
      });
      onDone();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-col gap-2 border-b border-gu-line pb-3">
      <p>
        <span className="font-semibold">{request.title}</span> · {slotWho(request)}
      </p>
      <p className="text-caption text-gu-ink-muted">
        {formatDate(request.startsAt)}, {formatTime(request.startsAt)}–{formatTime(request.endsAt)}{' '}
        · pedido a {formatDateTime(request.createdAt)}
      </p>
      {request.requestNote ? <p className="whitespace-pre-line">{request.requestNote}</p> : null}
      <Field label="Resposta" optional hint="Vai no email ao streamer. Para recusar, diz porquê.">
        <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {proposing ? (
        <div className="flex flex-wrap gap-3">
          <Field label="Nova hora: início">
            <DateTimeInput value={startsAt} onChange={setStartsAt} />
          </Field>
          <Field label="Fim">
            <DateTimeInput value={endsAt} onChange={setEndsAt} />
          </Field>
        </div>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flex flex-wrap gap-2">
        {proposing ? (
          <Button size="sm" disabled={busy} onClick={() => void decide('propose')}>
            Enviar proposta
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={busy} onClick={() => void decide('accept')}>
              Aceitar
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setProposing(true)}
            >
              Propor outra hora
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || note.trim() === ''}
              onClick={() => void decide('decline')}
            >
              Recusar
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

function NewSlot({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const create = async () => {
    const starts = lisbonInputToIso(startsAt);
    const ends = lisbonInputToIso(endsAt);
    if (starts === null || ends === null) {
      setFields({
        ...(starts === null ? { startsAt: 'Indique a data e a hora.' } : {}),
        ...(ends === null ? { endsAt: 'Indique a data e a hora.' } : {}),
      });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await send('POST', '/api/streaming/channel/slots', {
        title,
        startsAt: starts,
        endsAt: ends,
        streamerId: null,
      });
      setTitle('');
      onCreated();
    } catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiError) setFields(e.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Novo programa">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          Programas da casa (torneios, stream parties, conversas). Os dos streamers chegam pelos
          pedidos.
        </p>
        <Field label="Título" error={fields.title}>
          <Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-3">
          <Field label="Início" error={fields.startsAt}>
            <DateTimeInput value={startsAt} onChange={setStartsAt} />
          </Field>
          <Field label="Fim" error={fields.endsAt}>
            <DateTimeInput value={endsAt} onChange={setEndsAt} />
          </Field>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div>
          <Button disabled={busy || title.trim() === ''} onClick={() => void create()}>
            Marcar na grelha
          </Button>
        </div>
      </div>
    </Card>
  );
}
